import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import studentService from '../services/studentService';

const StudentsPage = () => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

  // Helper function to get the correct image URL
  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    const baseUrl = apiBaseUrl.replace('/api', ''); // Remove /api suffix
    return `${baseUrl}${photoUrl}`;
  };

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [activeTab, setActiveTab] = useState('personal'); // New tab state
  const [expandedDocument, setExpandedDocument] = useState(null); // New state for expanded documents
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [studentAgreements, setStudentAgreements] = useState([]);
  const [agreementFile, setAgreementFile] = useState(null);
  const [agreementType, setAgreementType] = useState('hostel');
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null); // 'all' | 'hostel' | 'mess' | 'pending'

  const [formData, setFormData] = useState({
    username: '', email: '', password: '', first_name: '', last_name: '',
    phone: '', date_of_birth: '', gender: '', address: '', city: '',
    state: '', pincode: '', parent_name: '', parent_phone: '', parent_email: '', 
    parent_relation: '', aadhaar_number: '', class_tuition_name: '', course: '', year_of_study: '',
  });

  // Document verification state
  const [docVerification, setDocVerification] = useState({
    profile_photo: { verified: false, notes: '' },
    aadhaar: { verified: false, notes: '' },
    address_proof: { verified: false, notes: '' },
  });

  const [verifyingDocuments, setVerifyingDocuments] = useState(false);
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [emailFormData, setEmailFormData] = useState({ id: null, email: '', first_name: '', last_name: '' });
  const [updatingEmail, setUpdatingEmail] = useState(false);

  const loadStudents = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await studentService.getStudents({});
      console.log('Students response:', response);
      const studentsList = response.students || response.data?.students || [];
      const sorted = Array.isArray(studentsList) ? [...studentsList].sort((a, b) => {
        const aTime = a.updated_at || a.created_at || '';
        const bTime = b.updated_at || b.created_at || '';
        return bTime.localeCompare(aTime);
      }) : [];
      setStudents(sorted);
      setError(null);
    } catch (err) {
      console.error('Error loading students:', err);
      setError(err.response?.data?.message || 'Failed to load students.');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStudents(); }, [loadStudents]);

  const handleCardFilter = (filter) => {
    setActiveFilter(prev => prev === filter ? null : filter);
  };

  const filteredStudents = (() => {
    let list = students;
    if (activeFilter === 'hostel') list = list.filter(s => s.hostel_admission_status === 'active' || s.hostel_admission_status === 'subscription_ended');
    else if (activeFilter === 'mess') list = list.filter(s => s.mess_admission_status === 'active' || s.mess_admission_status === 'subscription_ended');
    else if (activeFilter === 'pending') list = list.filter(s => s.hostel_admission_status !== 'active' && s.hostel_admission_status !== 'subscription_ended' && s.mess_admission_status !== 'active' && s.mess_admission_status !== 'subscription_ended');
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      list = list.filter((s) =>
        (s.user?.first_name || '').toLowerCase().includes(q) ||
        (s.user?.last_name || '').toLowerCase().includes(q) ||
        (`${s.user?.first_name || ''} ${s.user?.last_name || ''}`).toLowerCase().includes(q) ||
        (s.user?.email || '').toLowerCase().includes(q) ||
        (s.user?.phone || '').toLowerCase().includes(q) ||
        (s.registration_number || '').toLowerCase().includes(q)
      );
    }
    return list;
  })();

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        if (expandedDocument) {
          setExpandedDocument(null);
        } else if (showAgreementModal) {
          setShowAgreementModal(false);
        } else if (showEditEmailModal) {
          setShowEditEmailModal(false);
        } else if (showModal) {
          setShowModal(false);
        }
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => window.removeEventListener('keydown', handleEscapeKey);
  }, [showModal, expandedDocument, showAgreementModal, showEditEmailModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const openAddModal = () => {
    setFormData({ username: '', email: '', password: '', first_name: '', last_name: '', phone: '', date_of_birth: '', gender: '', address: '', city: '', state: '', pincode: '', parent_name: '', parent_phone: '', parent_email: '', parent_relation: '', aadhaar_number: '', class_tuition_name: '', course: '', year_of_study: '' });
    setModalMode('add');
    setShowModal(true);
  };

  const openEditEmailModal = (student) => {
    setEmailFormData({
      id: student.id,
      email: student.user?.email || '',
      first_name: student.user?.first_name || '',
      last_name: student.user?.last_name || ''
    });
    setShowEditEmailModal(true);
  };

  const handleEditEmailSubmit = async (e) => {
    e.preventDefault();
    if (!emailFormData.email.trim()) {
      alert('Email ID cannot be empty');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailFormData.email.trim())) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      setUpdatingEmail(true);
      await studentService.updateStudent(emailFormData.id, {
        email: emailFormData.email.trim()
      });
      alert('Student email ID updated successfully!');
      setShowEditEmailModal(false);
      loadStudents();
    } catch (err) {
      console.error('Error updating student email:', err);
      alert(err.response?.data?.message || 'Failed to update email ID');
    } finally {
      setUpdatingEmail(false);
    }
  };

  const openViewModal = async (student) => {
    console.log('Opening view modal for student:', student.id, student.user?.first_name);
    setSelectedStudent(student);
    setActiveTab('personal');
    setModalMode('view');
    setShowModal(true);

    // Fetch fresh student data so verification status reflects latest changes (e.g. from mobile app)
    try {
      const freshStudent = await studentService.getStudent(student.id);
      setSelectedStudent(freshStudent);
      setDocVerification({
        profile_photo: { 
          verified: freshStudent.profile_photo_verified || false, 
          notes: freshStudent.profile_photo_notes || '' 
        },
        aadhaar: { 
          verified: freshStudent.aadhaar_verified || false, 
          notes: freshStudent.aadhaar_notes || '' 
        },
        address_proof: { 
          verified: freshStudent.address_proof_verified || false, 
          notes: freshStudent.address_proof_notes || '' 
        },
      });
    } catch (err) {
      console.error('Error fetching fresh student data:', err);
      // Fall back to cached data
      setDocVerification({
        profile_photo: { 
          verified: student.profile_photo_verified || false, 
          notes: student.profile_photo_notes || '' 
        },
        aadhaar: { 
          verified: student.aadhaar_verified || false, 
          notes: student.aadhaar_notes || '' 
        },
        address_proof: { 
          verified: student.address_proof_verified || false, 
          notes: student.address_proof_notes || '' 
        },
      });
    }

    // Load agreements for this student
    console.log('Calling loadStudentAgreements with studentId:', student.id);
    loadStudentAgreements(student.id);
  };

  const loadStudentAgreements = async (studentId) => {
    if (!studentId) {
      console.warn('loadStudentAgreements: No studentId provided');
      setStudentAgreements([]);
      return;
    }
    
    const agreementUrl = `${apiBaseUrl}/agreements/admin/student/${studentId}/agreements`;
    console.log('Fetching agreements from:', agreementUrl);
    
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('No access token found');
        setStudentAgreements([]);
        return;
      }
      
      const response = await fetch(agreementUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Agreement API response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Agreements received:', data.agreements);
        setStudentAgreements(data.agreements || []);
      } else {
        const errorData = await response.text();
        console.error(`Agreement fetch error - Status: ${response.status}, Data:`, errorData);
        setStudentAgreements([]);
      }
    } catch (err) {
      console.error('Error fetching agreements:', err);
      setStudentAgreements([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'add') await studentService.createStudent(formData);
      else if (modalMode === 'edit') await studentService.updateStudent(selectedStudent.id, formData);
      setShowModal(false);
      loadStudents();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save student');
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target?.result;
        try {
          await studentService.uploadStudentPhoto(selectedStudent.id, base64Data);
          alert('Photo uploaded successfully');
          loadStudents();
          setSelectedStudent({ ...selectedStudent, profile_photo_url: `/api/students/photo/${Math.random()}` });
        } catch (err) {
          alert('Failed to upload photo: ' + (err.response?.data?.message || err.message));
        }
      };
      reader.readAsDataURL(file);
    } catch (err) {
      alert('Error reading file');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student? This will permanently delete ALL their data including hostel admissions, mess memberships, payments, and agreements.')) {
      try {
        await studentService.deleteStudent(id);
        loadStudents();
      } catch (err) {
        const msg = err.response?.data?.message || 'Failed to delete student';
        alert(msg);
      }
    }
  };

  const openAgreementModal = async (student) => {
    setSelectedStudent(student);
    setShowAgreementModal(true);
    setAgreementFile(null);
    setAgreementType('hostel');
    
    // Fetch existing agreements
    try {
      const response = await fetch(`${apiBaseUrl}/agreements/admin/student/${student.id}/agreements`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStudentAgreements(data.agreements || []);
      }
    } catch (err) {
      console.error('Error fetching agreements:', err);
      setStudentAgreements([]);
    }
  };

  const handleAgreementFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
      const validExtensions = ['pdf', 'jpg', 'jpeg', 'png', 'gif'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      
      if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
        alert('Please select a valid PDF or image file (PDF, JPG, PNG, GIF)');
        e.target.value = '';
        return;
      }
      setAgreementFile(file);
    }
  };

  const handleAgreementUpload = async (e) => {
    e.preventDefault();
    
    if (!agreementFile) {
      alert('Please select a file');
      return;
    }

    // Check admission status - values are strings like 'active', 'not_applied', etc.
    if (agreementType === 'hostel' && selectedStudent.hostel_admission_status !== 'active') {
      alert('❌ Student is not admitted for hostel. Cannot upload hostel agreement.');
      return;
    }

    if (agreementType === 'mess' && selectedStudent.mess_admission_status !== 'active') {
      alert('❌ Student is not admitted for mess. Cannot upload mess agreement.');
      return;
    }

    // File size validation (5 MB max)
    const fileSizeInMB = agreementFile.size / (1024 * 1024);
    if (fileSizeInMB > 5) {
      alert(`File size exceeds 5 MB limit. Selected file is ${fileSizeInMB.toFixed(2)} MB`);
      return;
    }
    
    const formData = new FormData();
    formData.append('agreement_type', agreementType);
    formData.append('file', agreementFile);
    
    try {
      setUploadingAgreement(true);
      
      const response = await fetch(`${apiBaseUrl}/agreements/admin/student/${selectedStudent.id}/upload-agreement`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      
      let data;
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.includes('application/json')) {
        // Response is not JSON - likely an error page
        const text = await response.text();
        console.error('Non-JSON response:', text);
        
        if (response.status === 500) {
          throw new Error('Server error occurred. Check browser console for details.');
        } else if (response.status === 401) {
          throw new Error('Authentication failed. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('Admin access required.');
        } else {
          throw new Error(`Server returned status ${response.status}`);
        }
      }
      
      data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || `Upload failed (Status: ${response.status})`);
      }
      
      // Handle both response formats
      const agreementData = data.agreement || data.data;
      if (agreementData) {
        setStudentAgreements([...studentAgreements, agreementData]);
      }
      
      setAgreementFile(null);
      setAgreementType('hostel');
      alert('Agreement uploaded successfully!');
      const fileInput = document.getElementById('agreementFileInput');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      console.error('Error uploading agreement:', err);
      console.error('Full error details:', {
        message: err.message,
        stack: err.stack
      });
      alert(err.message || 'Failed to upload agreement. Please try again.');
    } finally {
      setUploadingAgreement(false);
    }
  };

  const handleDeleteAgreement = async (agreementId) => {
    if (!window.confirm('Are you sure you want to delete this agreement?')) {
      return;
    }
    
    const deleteUrl = `${apiBaseUrl}/agreements/admin/agreement/${agreementId}`;
    console.log('Deleting agreement from:', deleteUrl);
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(deleteUrl, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Delete response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error(`Delete failed - Status: ${response.status}, Data:`, errorData);
        throw new Error('Delete failed');
      }
      
      setStudentAgreements(studentAgreements.filter(a => a.id !== agreementId));
      alert('Agreement deleted successfully!');
    } catch (err) {
      console.error('Error deleting agreement:', err);
      alert('Failed to delete agreement. Please try again.');
    }
  };

  const handleVerifyDocuments = async () => {
    if (!selectedStudent) return;
    
    try {
      setVerifyingDocuments(true);
      const verificationData = {};
      
      if (selectedStudent.profile_photo_url) {
        verificationData.profile_photo = docVerification.profile_photo;
      }
      if (selectedStudent.aadhaar_document_url) {
        verificationData.aadhaar = docVerification.aadhaar;
      }
      if (selectedStudent.address_proof_url) {
        verificationData.address_proof = docVerification.address_proof;
      }
      
      const response = await studentService.verifyDocuments(selectedStudent.id, verificationData);
      
      // Update selected student with new data
      setSelectedStudent(response.student);
      
      // Reload students list
      loadStudents();
      
      alert('Documents verified successfully!');
    } catch (err) {
      console.error('Error verifying documents:', err);
      alert(err.response?.data?.message || 'Failed to verify documents');
    } finally {
      setVerifyingDocuments(false);
    }
  };

  const handleSendVerificationReport = async () => {
    if (!selectedStudent) return;
    
    try {
      setVerifyingDocuments(true);
      const response = await studentService.sendVerificationReport(selectedStudent.id);
      
      if (response.success) {
        alert(`✅ Verification report sent to ${selectedStudent.user.first_name}!\n\nEmail: ${response.student_email}`);
      } else {
        alert('Failed to send verification report');
      }
    } catch (err) {
      console.error('Error sending report:', err);
      alert(err.response?.data?.message || 'Failed to send verification report');
    } finally {
      setVerifyingDocuments(false);
    }
  };

  const updateDocVerification = (docType, field, value) => {
    setDocVerification(prev => {
      const updated = {
        ...prev,
        [docType]: {
          ...prev[docType],
          [field]: value
        }
      };
      
      // Auto-clear notes when marking as verified
      if (field === 'verified' && value === true) {
        updated[docType].notes = '';
      }
      
      return updated;
    });
  };

  return (
    <Layout title="Students Management">
      <style>{`
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .header-actions { display: flex; gap: 12px; }
        .btn { padding: 10px 16px; border: none; border-radius: 8px; cursor: pointer; font-weight: 500; font-size: 14px; }
        .btn-primary { background: #84cc16; color: white; }
        .btn-primary:hover { background: #65a30d; }
        .btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #d1d5db; }
        .btn-danger { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
        .btn-sm { padding: 6px 12px; font-size: 13px; }
        .search-box { padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 8px; width: 280px; font-size: 14px; }
        .search-box:focus { outline: none; border-color: #84cc16; }
        .data-table { width: 100%; border-collapse: collapse; table-layout: auto; min-width: 1200px; }
        .data-table th { background: #f9fafb; padding: 12px 16px; text-align: left; font-size: 12px; font-weight: 600; text-transform: uppercase; color: #6b7280; border-bottom: 2px solid #e5e7eb; white-space: nowrap; }
        .data-table td { padding: 14px 16px; border-bottom: 1px solid #d1d5db; color: #374151; font-size: 14px; }
        .data-table tr { border-bottom: 1px solid #d1d5db; }
        .data-table tr:hover { background: #f3f4f6; }
        .status-badge { padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; }
        .status-active { background: #d1fae5; color: #047857; }
        .status-inactive { background: #fee2e2; color: #dc2626; }
        .actions { display: flex; gap: 8px; }
        .admission-badge { padding: 6px 10px; border-radius: 8px; font-size: 11px; font-weight: 600; white-space: nowrap; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal { background: white; border-radius: 16px; width: 100%; max-width: 800px; max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 40px -10px rgba(99, 102, 241, 0.25), 0 30px 50px -15px rgba(0, 0, 0, 0.15); border: 1px solid rgba(0, 0, 0, 0.04); }
        .modal-header { padding: 20px 24px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center; }
        .modal-header h2 { margin: 0; font-size: 18px; font-weight: 600; color: #1f2937; }
        .modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #6b7280; }
        .modal-body { padding: 24px; }
        .modal-footer { padding: 16px 24px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 12px; }
        .form-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
        .form-group { display: flex; flex-direction: column; }
        .form-group.full-width { grid-column: span 2; }
        .form-group label { margin-bottom: 6px; font-weight: 500; color: #374151; font-size: 13px; }
        .form-group input, .form-group select, .form-group textarea { padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; }
        .form-group input:focus, .form-group select:focus { outline: none; border-color: #84cc16; }
        .section-title { font-size: 14px; font-weight: 600; color: #84cc16; margin: 20px 0 12px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb; }
        .detail-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
        .detail-item { padding: 12px; background: white; border-radius: 12px; box-shadow: 0 4px 8px -2px rgba(99, 102, 241, 0.1), 0 6px 12px -4px rgba(0, 0, 0, 0.05); border: 1px solid rgba(0, 0, 0, 0.04); transition: all 0.2s ease; }
        .detail-item:hover { transform: translateY(-1px); box-shadow: 0 6px 12px -2px rgba(99, 102, 241, 0.15), 0 10px 16px -5px rgba(0, 0, 0, 0.06); }
        .detail-item label { display: block; font-size: 11px; color: #6b7280; margin-bottom: 4px; text-transform: uppercase; }
        .detail-item span { font-weight: 500; color: #1f2937; font-size: 14px; }
        .loading, .error, .empty { text-align: center; padding: 48px; color: #6b7280; }
        .error { color: #dc2626; }
        .profile-header { display: flex; align-items: center; gap: 20px; margin-bottom: 24px; padding: 20px; background: linear-gradient(135deg, #87ceeb 0%, #6db3d9 100%); border-radius: 16px; color: white; box-shadow: 0 10px 20px -4px rgba(135, 206, 235, 0.4), 0 15px 25px -8px rgba(0, 0, 0, 0.15); }
        .profile-photo { width: 100px; height: 100px; border-radius: 50%; object-fit: cover; border: 3px solid white; background: white; display: flex; align-items: center; justify-content: center; font-size: 40px; font-weight: bold; color: #667eea; }
        .profile-info h3 { margin: 0 0 8px 0; font-size: 24px; }
        .profile-info p { margin: 0; opacity: 0.9; }
        .profile-badge { display: inline-block; padding: 4px 12px; background: rgba(255,255,255,0.2); border-radius: 12px; font-size: 12px; margin-top: 8px; }
        .doc-status { display: flex; align-items: center; gap: 8px; }
        .doc-icon { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; }
        .doc-uploaded { background: #d1fae5; color: #047857; }
        .doc-missing { background: #fee2e2; color: #dc2626; }
      `}</style>

      <div className="page-header">
        <div className="header-actions">
          <input type="text" className="search-box" placeholder="Search students..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          <button className="btn btn-primary" onClick={openAddModal}>+ Add Student</button>
        </div>
      </div>

      {!loading && !error && students.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div
            onClick={() => handleCardFilter('all')}
            style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: activeFilter === 'all' ? '2px solid #0369a1' : '1px solid #bae6fd', cursor: 'pointer', boxShadow: activeFilter === 'all' ? '0 0 0 3px rgba(3,105,161,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
          >
            {activeFilter === 'all' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#0369a1', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
            <div style={{ fontSize: '12px', color: '#0369a1', fontWeight: '600', marginBottom: '4px' }}>Total Students</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#0c4a6e' }}>{students.length}</div>
          </div>
          <div
            onClick={() => handleCardFilter('hostel')}
            style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: activeFilter === 'hostel' ? '2px solid #166534' : '1px solid #bbf7d0', cursor: 'pointer', boxShadow: activeFilter === 'hostel' ? '0 0 0 3px rgba(22,101,52,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
          >
            {activeFilter === 'hostel' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#166534', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
            <div style={{ fontSize: '12px', color: '#166534', fontWeight: '600', marginBottom: '4px' }}>Hostel Admitted</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#15803d' }}>{students.filter(s => s.hostel_admission_status === 'active' || s.hostel_admission_status === 'subscription_ended').length}</div>
          </div>
          <div
            onClick={() => handleCardFilter('mess')}
            style={{ padding: '16px', background: '#fdf2f8', borderRadius: '8px', border: activeFilter === 'mess' ? '2px solid #be185d' : '1px solid #fbcfe8', cursor: 'pointer', boxShadow: activeFilter === 'mess' ? '0 0 0 3px rgba(190,24,93,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
          >
            {activeFilter === 'mess' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#be185d', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
            <div style={{ fontSize: '12px', color: '#be185d', fontWeight: '600', marginBottom: '4px' }}>Mess Admitted</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#be185d' }}>{students.filter(s => s.mess_admission_status === 'active' || s.mess_admission_status === 'subscription_ended').length}</div>
          </div>
          <div
            onClick={() => handleCardFilter('pending')}
            style={{ padding: '16px', background: '#fffbeb', borderRadius: '8px', border: activeFilter === 'pending' ? '2px solid #92400e' : '1px solid #fde68a', cursor: 'pointer', boxShadow: activeFilter === 'pending' ? '0 0 0 3px rgba(146,64,14,0.15)' : 'none', transition: 'box-shadow 0.15s, border 0.15s', position: 'relative' }}
          >
            {activeFilter === 'pending' && <div style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '10px', background: '#92400e', color: '#fff', borderRadius: '4px', padding: '1px 6px' }}>Filtered</div>}
            <div style={{ fontSize: '12px', color: '#92400e', fontWeight: '600', marginBottom: '4px' }}>Pending Admissions</div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#b45309' }}>{students.filter(s => s.hostel_admission_status !== 'active' && s.hostel_admission_status !== 'subscription_ended' && s.mess_admission_status !== 'active' && s.mess_admission_status !== 'subscription_ended').length}</div>
          </div>
        </div>
      )}

      {activeFilter && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', padding: '8px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
          <span style={{ fontSize: '13px', color: '#475569' }}>
            Showing <strong>{filteredStudents.length}</strong> student{filteredStudents.length !== 1 ? 's' : ''} —{' '}
            {activeFilter === 'all' && 'All Students'}
            {activeFilter === 'hostel' && 'Hostel Admitted (incl. Subscription Ended)'}
            {activeFilter === 'mess' && 'Mess Admitted (incl. Subscription Ended)'}
            {activeFilter === 'pending' && 'Pending Admissions (no hostel or mess)'}
          </span>
          <button
            onClick={() => setActiveFilter(null)}
            style={{ marginLeft: 'auto', fontSize: '12px', padding: '3px 10px', border: '1px solid #cbd5e1', borderRadius: '5px', background: '#fff', cursor: 'pointer', color: '#64748b' }}
          >✕ Clear Filter</button>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading students...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : filteredStudents.length === 0 ? (
        <div className="empty">{searchTerm ? `No students found matching "${searchTerm}"` : activeFilter ? 'No students match this filter' : 'No students found'}</div>
      ) : (
        <div style={{ overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Sr. No.</th>
                <th>Photo</th>
                <th>Registration No</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Registration Date</th>
                <th>Class</th>
                <th>Status</th>
                <th>Hostel Admission</th>
                <th>Mess Admission</th>
                <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map((student, index) => (
              <tr key={student.id}>
                <td>{index + 1}</td>
                <td>
                  {student.profile_photo_url ? (
                    <img 
                      src={getImageUrl(student.profile_photo_url)} 
                      alt="" 
                      style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#667eea', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                      {student.user?.first_name?.[0]?.toUpperCase() || 'U'}
                    </div>
                  )}
                </td>
                <td><strong>{student.registration_number || 'N/A'}</strong></td>
                <td><strong>{student.user?.first_name} {student.user?.last_name}</strong></td>
                <td>{student.user?.email}</td>
                <td>{student.user?.phone}</td>
                <td>
                  {student.app_registration_date ? 
                    new Date(student.app_registration_date).toLocaleString('en-IN', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'N/A'
                  }
                </td>
                <td>{student.class_tuition_name || student.city || 'N/A'}</td>
                <td>
                  <span className={`status-badge ${student.is_active ? 'status-active' : 'status-inactive'}`}>
                    {student.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: student.hostel_admission_status === 'active' ? '#d1fae5' :
                                      student.hostel_admission_status === 'subscription_ended' ? '#fee2e2' :
                                      student.hostel_admission_status === 'pending' ? '#fef3c7' :
                                      student.hostel_admission_status === 'rejected' ? '#fee2e2' : '#f0f0f0',
                    color: student.hostel_admission_status === 'active' ? '#047857' :
                           student.hostel_admission_status === 'subscription_ended' ? '#991b1b' :
                           student.hostel_admission_status === 'pending' ? '#92400e' :
                           student.hostel_admission_status === 'rejected' ? '#dc2626' : '#666666'
                  }}>
                    {student.hostel_admission_status === 'active' ? '✓ Admitted' :
                     student.hostel_admission_status === 'subscription_ended' ? 'Subscription Ended' :
                     student.hostel_admission_status === 'pending' ? '⏳ Pending' :
                     student.hostel_admission_status === 'rejected' ? '✗ Rejected' : 'Not Applied'}
                  </span>
                </td>
                <td>
                  <span style={{
                    padding: '4px 10px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: student.mess_admission_status === 'active' ? '#d1fae5' :
                                      student.mess_admission_status === 'subscription_ended' ? '#fee2e2' :
                                      student.mess_admission_status === 'pending' ? '#fef3c7' :
                                      student.mess_admission_status === 'rejected' ? '#fee2e2' : '#f0f0f0',
                    color: student.mess_admission_status === 'active' ? '#047857' :
                           student.mess_admission_status === 'subscription_ended' ? '#991b1b' :
                           student.mess_admission_status === 'pending' ? '#92400e' :
                           student.mess_admission_status === 'rejected' ? '#dc2626' : '#666666'
                  }}>
                    {student.mess_admission_status === 'active' ? '✓ Admitted' :
                     student.mess_admission_status === 'subscription_ended' ? 'Subscription Ended' :
                     student.mess_admission_status === 'pending' ? '⏳ Pending' :
                     student.mess_admission_status === 'rejected' ? '✗ Rejected' : 'Not Applied'}
                  </span>
                </td>
                <td>
                  <div className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => openViewModal(student)}>View</button>
                    <button className="btn btn-primary btn-sm" onClick={() => openAgreementModal(student)}>Agreement</button>
                    <button className="btn btn-secondary btn-sm" style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #bae6fd' }} onClick={() => openEditEmailModal(student)}>Edit Email</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(student.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'view' ? 'Student Details' : 'Add New Student'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {modalMode === 'view' ? (
                <div>
                  {/* Profile Header with Photo */}
                  <div className="profile-header">
                    {selectedStudent?.profile_photo_url ? (
                      <img 
                        src={getImageUrl(selectedStudent.profile_photo_url)} 
                        alt="Profile" 
                        className="profile-photo"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <div className="profile-photo" style={{ display: selectedStudent?.profile_photo_url ? 'none' : 'flex' }}>
                      {selectedStudent?.user?.first_name?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="profile-info">
                      <h3>{selectedStudent?.user?.first_name} {selectedStudent?.user?.last_name}</h3>
                      <p>{selectedStudent?.user?.email}</p>
                      <p>{selectedStudent?.user?.phone}</p>
                      <span className="profile-badge">{selectedStudent?.user?.role?.toUpperCase() || 'STUDENT'}</span>
                    </div>
                  </div>
                  
                  <div className="section-title">Personal Information</div>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Registration Number</label><span style={{ fontFamily: 'monospace', fontWeight: '600', color: '#667eea' }}>{selectedStudent?.registration_number || 'N/A'}</span></div>
                    <div className="detail-item"><label>Full Name</label><span>{selectedStudent?.user?.first_name} {selectedStudent?.user?.last_name}</span></div>
                    <div className="detail-item"><label>Username</label><span>{selectedStudent?.user?.username || 'N/A'}</span></div>
                    <div className="detail-item"><label>Email</label><span>{selectedStudent?.user?.email}</span></div>
                    <div className="detail-item"><label>Phone</label><span>{selectedStudent?.user?.phone}</span></div>
                    <div className="detail-item"><label>Date of Birth</label><span>{selectedStudent?.date_of_birth || 'N/A'}</span></div>
                    <div className="detail-item"><label>Gender</label><span>{selectedStudent?.gender || 'N/A'}</span></div>
                  </div>
                  
                  <div className="section-title">Address</div>
                  <div className="detail-grid">
                    <div className="detail-item" style={{ gridColumn: 'span 2' }}><label>Address</label><span>{selectedStudent?.address || 'N/A'}</span></div>
                    <div className="detail-item"><label>City</label><span>{selectedStudent?.city || 'N/A'}</span></div>
                    <div className="detail-item"><label>State</label><span>{selectedStudent?.state || 'N/A'}</span></div>
                    <div className="detail-item"><label>Pincode</label><span>{selectedStudent?.pincode || 'N/A'}</span></div>
                  </div>
                  
                  <div className="section-title">Education</div>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Class/Tuition Name</label><span>{selectedStudent?.class_tuition_name || 'N/A'}</span></div>
                    <div className="detail-item"><label>Course</label><span>{selectedStudent?.course || 'N/A'}</span></div>
                    <div className="detail-item"><label>Year of Study</label><span>{selectedStudent?.year_of_study || 'N/A'}</span></div>
                  </div>
                  
                  <div className="section-title">Admission Status</div>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <label>Hostel Admission</label>
                      <span style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        display: 'inline-block',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: selectedStudent?.hostel_admission_status === 'active' ? '#d1fae5' :
                                          selectedStudent?.hostel_admission_status === 'subscription_ended' ? '#fee2e2' :
                                          selectedStudent?.hostel_admission_status === 'pending' ? '#fef3c7' :
                                          selectedStudent?.hostel_admission_status === 'rejected' ? '#fee2e2' : '#f0f0f0',
                        color: selectedStudent?.hostel_admission_status === 'active' ? '#047857' :
                               selectedStudent?.hostel_admission_status === 'subscription_ended' ? '#991b1b' :
                               selectedStudent?.hostel_admission_status === 'pending' ? '#92400e' :
                               selectedStudent?.hostel_admission_status === 'rejected' ? '#dc2626' : '#666666'
                      }}>
                        {selectedStudent?.hostel_admission_status === 'active' ? '✓ Admitted' :
                         selectedStudent?.hostel_admission_status === 'subscription_ended' ? '⚠ Subscription Ended' :
                         selectedStudent?.hostel_admission_status === 'pending' ? '⏳ Pending' :
                         selectedStudent?.hostel_admission_status === 'rejected' ? '✗ Rejected' : '— Not Applied'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <label>Mess Admission</label>
                      <span style={{
                        padding: '6px 12px',
                        borderRadius: '8px',
                        display: 'inline-block',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: selectedStudent?.mess_admission_status === 'active' ? '#d1fae5' :
                                          selectedStudent?.mess_admission_status === 'subscription_ended' ? '#fee2e2' :
                                          selectedStudent?.mess_admission_status === 'pending' ? '#fef3c7' :
                                          selectedStudent?.mess_admission_status === 'rejected' ? '#fee2e2' : '#f0f0f0',
                        color: selectedStudent?.mess_admission_status === 'active' ? '#047857' :
                               selectedStudent?.mess_admission_status === 'subscription_ended' ? '#991b1b' :
                               selectedStudent?.mess_admission_status === 'pending' ? '#92400e' :
                               selectedStudent?.mess_admission_status === 'rejected' ? '#dc2626' : '#666666'
                      }}>
                        {selectedStudent?.mess_admission_status === 'active' ? '✓ Admitted' :
                         selectedStudent?.mess_admission_status === 'subscription_ended' ? '⚠ Subscription Ended' :
                         selectedStudent?.mess_admission_status === 'pending' ? '⏳ Pending' :
                         selectedStudent?.mess_admission_status === 'rejected' ? '✗ Rejected' : '— Not Applied'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="section-title">Enrollment & Admission Dates</div>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Date of Registration</label><span>{selectedStudent?.app_registration_date ? (() => {
                      const date = new Date(selectedStudent.app_registration_date);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })() : 'N/A'}</span></div>
                    <div className="detail-item"><label>Hostel Admission Date</label><span>{selectedStudent?.hostel_admission_date ? (() => {
                      const date = new Date(selectedStudent.hostel_admission_date);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })() : 'N/A'}</span></div>
                    <div className="detail-item"><label>Hostel Duration</label><span>{selectedStudent?.hostel_duration || 'N/A'}</span></div>
                    <div className="detail-item"><label>Hostel Name</label><span>{selectedStudent?.hostel_name || 'N/A'}</span></div>
                    <div className="detail-item"><label>Mess Enrollment Date</label><span>{selectedStudent?.mess_enrollment_date ? (() => {
                      const date = new Date(selectedStudent.mess_enrollment_date);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })() : 'N/A'}</span></div>
                    <div className="detail-item"><label>Mess Name</label><span>{selectedStudent?.mess_name || 'N/A'}</span></div>
                  </div>
                  
                  <div className="section-title">Parent/Guardian Information</div>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Parent Name</label><span>{selectedStudent?.parent_name || 'N/A'}</span></div>
                    <div className="detail-item"><label>Parent Phone</label><span>{selectedStudent?.parent_phone || 'N/A'}</span></div>
                    <div className="detail-item"><label>Parent Email</label><span>{selectedStudent?.parent_email || 'N/A'}</span></div>
                    <div className="detail-item"><label>Relation</label><span>{selectedStudent?.parent_relation || 'N/A'}</span></div>
                  </div>
                  
                  <div className="section-title">Documents & Verification</div>
                  
                  {/* Tabs Navigation */}
                  <div style={{ display: 'flex', gap: '4px', marginBottom: '16px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
                    <button 
                      onClick={() => setActiveTab('personal')}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: activeTab === 'personal' ? '#667eea' : '#6b7280',
                        borderBottom: activeTab === 'personal' ? '3px solid #667eea' : 'none',
                        fontWeight: activeTab === 'personal' ? '600' : '500',
                        fontSize: '14px',
                        marginBottom: '-12px'
                      }}
                    >
                      Documents Overview
                    </button>
                    <button 
                      onClick={() => setActiveTab('verification')}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: activeTab === 'verification' ? '#667eea' : '#6b7280',
                        borderBottom: activeTab === 'verification' ? '3px solid #667eea' : 'none',
                        fontWeight: activeTab === 'verification' ? '600' : '500',
                        fontSize: '14px',
                        marginBottom: '-12px'
                      }}
                    >
                      Verify Documents
                    </button>
                    <button 
                      onClick={() => setActiveTab('agreement')}
                      style={{
                        padding: '8px 16px',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        color: activeTab === 'agreement' ? '#667eea' : '#6b7280',
                        borderBottom: activeTab === 'agreement' ? '3px solid #667eea' : 'none',
                        fontWeight: activeTab === 'agreement' ? '600' : '500',
                        fontSize: '14px',
                        marginBottom: '-12px'
                      }}
                    >
                      Agreements
                    </button>
                  </div>

                  {/* Documents Overview Tab */}
                  {activeTab === 'personal' && (
                    <div className="detail-grid">
                      <div className="detail-item"><label>Aadhaar Number</label><span>{selectedStudent?.aadhaar_number ? `XXXX-XXXX-${selectedStudent.aadhaar_number.slice(-4)}` : 'N/A'}</span></div>
                      
                      <div className="detail-item">
                        <label>Profile Photo</label>
                        <div className="doc-status">
                          <span className={`doc-icon ${selectedStudent?.profile_photo_url ? 'doc-uploaded' : 'doc-missing'}`}>
                            {selectedStudent?.profile_photo_url ? '✓' : '✗'}
                          </span>
                          <span>{selectedStudent?.profile_photo_url ? 'Uploaded' : 'Not Uploaded'}</span>
                          {selectedStudent?.profile_photo_verified && (
                            <span style={{ marginLeft: '8px', color: '#047857', fontSize: '12px' }}>✓ Verified</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="detail-item">
                        <label>Aadhaar Document</label>
                        <div className="doc-status">
                          <span className={`doc-icon ${selectedStudent?.aadhaar_document_url ? 'doc-uploaded' : 'doc-missing'}`}>
                            {selectedStudent?.aadhaar_document_url ? '✓' : '✗'}
                          </span>
                          <span>{selectedStudent?.aadhaar_document_url ? 'Uploaded' : 'Not Uploaded'}</span>
                          {selectedStudent?.aadhaar_verified && (
                            <span style={{ marginLeft: '8px', color: '#047857', fontSize: '12px' }}>✓ Verified</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="detail-item">
                        <label>Address Proof</label>
                        <div className="doc-status">
                          <span className={`doc-icon ${selectedStudent?.address_proof_url ? 'doc-uploaded' : 'doc-missing'}`}>
                            {selectedStudent?.address_proof_url ? '✓' : '✗'}
                          </span>
                          <span>{selectedStudent?.address_proof_url ? 'Uploaded' : 'Not Uploaded'}</span>
                          {selectedStudent?.address_proof_verified && (
                            <span style={{ marginLeft: '8px', color: '#047857', fontSize: '12px' }}>✓ Verified</span>
                          )}
                        </div>
                      </div>

                      <div className="detail-item" style={{ gridColumn: 'span 2' }}>
                        <label>Overall Verification Status</label>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '600',
                          backgroundColor: selectedStudent?.documents_verification_status === 'verified' ? '#dcfce7' : 
                                           selectedStudent?.documents_verification_status === 'rejected' ? '#fee2e2' : '#fef3c7',
                          color: selectedStudent?.documents_verification_status === 'verified' ? '#166534' : 
                                selectedStudent?.documents_verification_status === 'rejected' ? '#991b1b' : '#92400e',
                          textTransform: 'uppercase'
                        }}>
                          {selectedStudent?.documents_verification_status || 'None'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Document Verification Tab */}
                  {activeTab === 'verification' && (
                    <div>
                      <div style={{ backgroundColor: '#f0f9ff', padding: '12px', borderRadius: '8px', marginBottom: '16px' }}>
                        <p style={{ margin: '0', fontSize: '13px', color: '#0369a1' }}>
                          📋 Review each document and verify if it meets requirements. Add notes for rejected documents.
                        </p>
                      </div>

                      {selectedStudent?.profile_photo_url && (
                        <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                          <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>Profile Photo</h4>
                          <img 
                            src={getImageUrl(selectedStudent.profile_photo_url)} 
                            alt="Profile Photo"
                            onClick={() => setExpandedDocument({ url: getImageUrl(selectedStudent.profile_photo_url), title: 'Profile Photo' })}
                            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', marginBottom: '12px', cursor: 'pointer', transition: 'transform 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                          <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <input 
                                type="checkbox"
                                checked={docVerification.profile_photo.verified}
                                onChange={(e) => updateDocVerification('profile_photo', 'verified', e.target.checked)}
                              />
                              <span>✓ Verified</span>
                            </label>
                            <textarea
                              placeholder="Add notes (if rejecting, explain why)..."
                              value={docVerification.profile_photo.notes}
                              onChange={(e) => updateDocVerification('profile_photo', 'notes', e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: '80px',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '13px'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {selectedStudent?.aadhaar_document_url && (
                        <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                          <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>Aadhaar Document</h4>
                          <img 
                            src={getImageUrl(selectedStudent.aadhaar_document_url)} 
                            alt="Aadhaar Document"
                            onClick={() => setExpandedDocument({ url: getImageUrl(selectedStudent.aadhaar_document_url), title: 'Aadhaar Document' })}
                            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', marginBottom: '12px', cursor: 'pointer', transition: 'transform 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                          <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <input 
                                type="checkbox"
                                checked={docVerification.aadhaar.verified}
                                onChange={(e) => updateDocVerification('aadhaar', 'verified', e.target.checked)}
                              />
                              <span>✓ Verified</span>
                            </label>
                            <textarea
                              placeholder="Add notes (if rejecting, explain why)..."
                              value={docVerification.aadhaar.notes}
                              onChange={(e) => updateDocVerification('aadhaar', 'notes', e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: '80px',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '13px'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {selectedStudent?.address_proof_url && (
                        <div style={{ marginBottom: '16px', padding: '12px', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                          <h4 style={{ margin: '0 0 12px 0', color: '#374151' }}>Address Proof</h4>
                          <img 
                            src={getImageUrl(selectedStudent.address_proof_url)} 
                            alt="Address Proof"
                            onClick={() => setExpandedDocument({ url: getImageUrl(selectedStudent.address_proof_url), title: 'Address Proof' })}
                            style={{ maxWidth: '200px', maxHeight: '200px', borderRadius: '8px', marginBottom: '12px', cursor: 'pointer', transition: 'transform 0.2s' }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                          <div>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <input 
                                type="checkbox"
                                checked={docVerification.address_proof.verified}
                                onChange={(e) => updateDocVerification('address_proof', 'verified', e.target.checked)}
                              />
                              <span>✓ Verified</span>
                            </label>
                            <textarea
                              placeholder="Add notes (if rejecting, explain why)..."
                              value={docVerification.address_proof.notes}
                              onChange={(e) => updateDocVerification('address_proof', 'notes', e.target.value)}
                              style={{
                                width: '100%',
                                minHeight: '80px',
                                padding: '8px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Arial, sans-serif',
                                fontSize: '13px'
                              }}
                            />
                          </div>
                        </div>
                      )}

                      <button 
                        onClick={handleVerifyDocuments}
                        disabled={verifyingDocuments}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#84cc16',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: verifyingDocuments ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          marginTop: '16px',
                          opacity: verifyingDocuments ? 0.6 : 1
                        }}
                      >
                        {verifyingDocuments ? 'Saving...' : '💾 Save Verification'}
                      </button>
                      
                      <button
                        disabled={verifyingDocuments}
                        onClick={handleSendVerificationReport}
                        style={{
                          padding: '10px 20px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: verifyingDocuments ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          marginTop: '16px',
                          marginLeft: '10px',
                          opacity: verifyingDocuments ? 0.6 : 1
                        }}
                      >
                        {verifyingDocuments ? 'Sending...' : '📧 Send Report to Student'}
                      </button>
                    </div>
                  )}

                  {/* Agreements Tab */}
                  {activeTab === 'agreement' && (
                    <div style={{ paddingTop: '16px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                        Agreements for {selectedStudent?.user?.first_name || selectedStudent?.first_name || 'Student'} {selectedStudent?.user?.last_name || selectedStudent?.last_name || ''}
                      </h3>
                      
                      <button 
                        onClick={() => openAgreementModal(selectedStudent)}
                        style={{
                          marginBottom: '16px',
                          padding: '10px 16px',
                          backgroundColor: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        + Upload New Agreement
                      </button>
                      
                      {studentAgreements.length === 0 ? (
                        <div style={{ 
                          padding: '24px', 
                          backgroundColor: '#f9fafb', 
                          borderRadius: '8px', 
                          textAlign: 'center',
                          border: '1px solid #e5e7eb'
                        }}>
                          <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                            No agreements uploaded yet for this student
                          </p>
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gap: '12px' }}>
                          {studentAgreements.map((agreement) => {
                            console.log('Rendering agreement:', agreement);
                            console.log('Selected student for name:', selectedStudent);
                            return (
                            <div 
                              key={agreement.id} 
                              style={{
                                padding: '16px',
                                backgroundColor: '#f9fafb',
                                borderRadius: '8px',
                                border: '1px solid #e5e7eb',
                                display: 'grid',
                                gridTemplateColumns: '1fr auto',
                                gap: '16px'
                              }}
                            >
                              <div>
                                <h4 style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                                  {agreement.agreement_type?.charAt(0).toUpperCase() + agreement.agreement_type?.slice(1) || 'Agreement'} Agreement
                                </h4>
                                <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280' }}>
                                  <strong>Student:</strong> {selectedStudent?.user?.first_name || selectedStudent?.first_name || 'N/A'} {selectedStudent?.user?.last_name || selectedStudent?.last_name || ''}
                                </p>
                                <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#6b7280' }}>
                                  <strong>Reference #:</strong> {agreement.agreement_number || `AGR-${agreement.id}`}
                                </p>
                                <p style={{ margin: '0', fontSize: '12px', color: '#6b7280' }}>
                                  <strong>Uploaded:</strong> {(() => {
                    const date = new Date(agreement.created_at);
                    const day = String(date.getDate()).padStart(2, '0');
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const year = date.getFullYear();
                    return `${day}/${month}/${year}`;
                  })()}
                                </p>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
                                {agreement.scanned_copy_url && (
                                  <>
                                    <a 
                                      href={`${apiBaseUrl}/agreements/${agreement.id}/download`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      download
                                      style={{
                                        padding: '8px 14px',
                                        backgroundColor: '#e0e7ff',
                                        color: '#667eea',
                                        textDecoration: 'none',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                        border: '1px solid #c7d2fe'
                                      }}
                                    >
                                      📥 Download
                                    </a>
                                    <a 
                                      href={`${apiBaseUrl}/agreements/${agreement.id}/download`} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{
                                        padding: '8px 14px',
                                        backgroundColor: '#dbeafe',
                                        color: '#0284c7',
                                        textDecoration: 'none',
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        whiteSpace: 'nowrap',
                                        border: '1px solid #bae6fd'
                                      }}
                                    >
                                      👁️ View
                                    </a>
                                  </>
                                )}
                                <button 
                                  onClick={() => handleDeleteAgreement(agreement.id)}
                                  style={{
                                    padding: '8px 14px',
                                    backgroundColor: '#fee2e2',
                                    color: '#dc2626',
                                    border: '1px solid #fecaca',
                                    borderRadius: '6px',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  🗑️ Delete
                                </button>
                              </div>
                            </div>
                          )})}
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="section-title">Registration & Payment</div>
                  <div className="detail-grid">
                    <div className="detail-item"><label>Account Status</label><span style={{ color: selectedStudent?.is_active ? '#047857' : '#dc2626' }}>{selectedStudent?.is_active ? 'Active' : 'Inactive'}</span></div>
                    <div className="detail-item"><label>Registration Status</label><span style={{ textTransform: 'uppercase' }}>{selectedStudent?.user?.registration_status || 'N/A'}</span></div>
                  </div>
                </div>
              ) : null}
            </div>
            {modalMode !== 'view' && (
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSubmit}>Create Student</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agreement Upload Modal */}
      {showAgreementModal && selectedStudent && (
        <div className="modal-overlay" onClick={() => setShowAgreementModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2>Manage Agreements - {selectedStudent?.user?.first_name} {selectedStudent?.user?.last_name}</h2>
              <button className="modal-close" onClick={() => setShowAgreementModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {/* Upload Form */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>Upload New Agreement</h3>
                <form onSubmit={handleAgreementUpload}>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>Agreement Type</label>
                    <select 
                      value={agreementType} 
                      onChange={(e) => setAgreementType(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        fontSize: '14px',
                        fontFamily: 'inherit'
                      }}
                    >
                      <option value="hostel">Hostel Agreement</option>
                      <option value="mess">Mess Agreement</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>Select File (PDF or Image)</label>
                    <input 
                      id="agreementFileInput"
                      type="file" 
                      accept=".pdf,.jpg,.jpeg,.png,.gif" 
                      onChange={handleAgreementFileChange}
                      disabled={uploadingAgreement}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                        fontSize: '14px'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '6px 0 0 0' }}>
                      Accepted formats: PDF, JPG, PNG, GIF
                    </p>
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px 10px', 
                      backgroundColor: '#fef3c7', 
                      borderRadius: '6px',
                      borderLeft: '4px solid #d97706'
                    }}>
                      <p style={{ margin: 0, fontSize: '12px', color: '#92400e', fontWeight: '500' }}>
                        ⚠️ File size must be less than 5 MB
                      </p>
                    </div>
                  </div>
                  
                  {agreementFile && (
                    <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#f0fdf4', borderRadius: '6px', borderLeft: '4px solid #22c55e' }}>
                      <p style={{ margin: 0, fontSize: '14px', color: '#15803d', fontWeight: '500' }}>
                        ✓ {agreementFile.name} ({(agreementFile.size / 1024).toFixed(2)} KB)
                      </p>
                    </div>
                  )}
                  
                  <button 
                    type="submit"
                    disabled={!agreementFile || uploadingAgreement}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      backgroundColor: uploadingAgreement || !agreementFile ? '#d1d5db' : '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: uploadingAgreement || !agreementFile ? 'not-allowed' : 'pointer',
                      opacity: uploadingAgreement || !agreementFile ? 0.6 : 1
                    }}
                  >
                    {uploadingAgreement ? 'Uploading...' : 'Upload Agreement'}
                  </button>
                </form>
              </div>
              
              {/* Existing Agreements List */}
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                  Existing Agreements ({studentAgreements.length})
                </h3>
                
                {studentAgreements.length === 0 ? (
                  <p style={{ color: '#6b7280', fontSize: '14px', textAlign: 'center', padding: '16px 0' }}>
                    No agreements uploaded yet
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {studentAgreements.map((agreement) => (
                      <div 
                        key={agreement.id} 
                        style={{
                          padding: '12px',
                          backgroundColor: '#f9fafb',
                          borderRadius: '6px',
                          border: '1px solid #e5e7eb',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: '#374151', fontSize: '14px' }}>
                            {agreement.agreement_type?.charAt(0).toUpperCase() + agreement.agreement_type?.slice(1) || 'Agreement'} - #{agreement.agreement_number}
                          </p>
                          <p style={{ margin: '0', fontSize: '12px', color: '#6b7280' }}>
                            Uploaded: {(() => {
                      const date = new Date(agreement.created_at);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}
                          </p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {agreement.scanned_copy_url && (
                            <a 
                              href={`${apiBaseUrl.replace('/api', '')}${agreement.scanned_copy_url}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#e0e7ff',
                                color: '#667eea',
                                textDecoration: 'none',
                                borderRadius: '4px',
                                fontSize: '12px',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              View
                            </a>
                          )}
                          <button 
                            onClick={() => handleDeleteAgreement(agreement.id)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#fee2e2',
                              color: '#dc2626',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAgreementModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Expanded Document Modal */}
      {expandedDocument && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <button
              onClick={() => setExpandedDocument(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '0',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
            
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#374151' }}>
              {expandedDocument.title}
            </h3>
            
            <img 
              src={expandedDocument.url} 
              alt={expandedDocument.title}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                borderRadius: '8px',
                marginBottom: '16px'
              }}
            />
            
            <a 
              href={expandedDocument.url} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '8px 16px',
                backgroundColor: '#667eea',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                alignSelf: 'flex-start',
                marginTop: 'auto'
              }}
            >
              Open in New Tab
            </a>
          </div>
        </div>
      )}

      {showEditEmailModal && (
        <div className="modal-overlay" onClick={() => setShowEditEmailModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '440px' }}>
            <div className="modal-header">
              <h2>Edit Email - {emailFormData.first_name} {emailFormData.last_name}</h2>
              <button className="modal-close" onClick={() => setShowEditEmailModal(false)}>×</button>
            </div>
            <form onSubmit={handleEditEmailSubmit}>
              <div className="modal-body">
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#374151' }}>Student Email ID</label>
                  <input 
                    type="email" 
                    required
                    value={emailFormData.email} 
                    onChange={(e) => setEmailFormData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={updatingEmail}
                    placeholder="Enter new email address"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d1d5db',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditEmailModal(false)} disabled={updatingEmail}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={updatingEmail}>
                  {updatingEmail ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default StudentsPage;
